/**
 * Your own account.
 *
 * Reached from the name in the corner of the sidebar, which is where people
 * look for it. Everything here comes from the session the shell already holds,
 * so opening it costs no request.
 *
 * The display name is yours to change. The email address is not: it is what the
 * identity provider asserted when you signed in, and what any access granted to
 * you was written against - so it changes by signing in with a different
 * account, not by typing here.
 */
import { ROLE_LABEL } from '../roles.ts';
import { useState } from 'react';
import { useOutletContext, useRevalidator } from 'react-router';
import type { Session } from '../api.ts';
import { patch } from '../api.ts';
import { Card, Detail, Details, Empty, Pill, Table } from '../components/ui.tsx';
import { EditPanel, Field } from '../components/EditPanel.tsx';
import { ImageUpload } from '../components/ImageUpload.tsx';
import { PasswordCard } from '../components/PasswordCard.tsx';

export default function Profile() {
  const session = useOutletContext<Session>();
  const revalidator = useRevalidator();
  const isPlatformAdmin = session.user.platformRole === 'JMC_ADMIN';

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(session.user.displayName);

  const save = async () => {
    await patch('/auth/me', { displayName });
    // The name is in the sidebar too, and that comes from the root loader.
    await revalidator.revalidate();
  };

  return (
    <>
      <Card
        title="プロフィール"
        caption="表示名は変更できます。メールアドレスはログインに使うGoogleアカウントのものです。"
        actions={
          !editing && (
            <button className="btn btn--sm" type="button" onClick={() => setEditing(true)}>
              表示名を変更
            </button>
          )
        }
      >
        <Details>
          <Detail label="プロフィール画像" hint="サイドバーと一覧に表示されます。">
            <ImageUpload
              imageId={session.user.avatarId}
              endpoint="/auth/me/avatar"
              label="画像を選ぶ"
              placeholder={(session.user.displayName || session.user.email || '?')
                .trim()
                .charAt(0)
                .toUpperCase()}
              onDone={() => revalidator.revalidate()}
            />
          </Detail>
          <Detail label="表示名">{session.user.displayName || '—'}</Detail>
          <Detail
            label="メールアドレス"
            hint="変更するには、別のGoogleアカウントでログインしてください。"
          >
            {session.user.email ?? '—'}
          </Detail>
          <Detail label="区分">
            {isPlatformAdmin ? (
              <Pill tone="danger">JMC管理者</Pill>
            ) : (
              <Pill>クリニックユーザー</Pill>
            )}
          </Detail>
        </Details>

        <EditPanel open={editing} onClose={() => setEditing(false)} onSubmit={save}>
          <Field label="表示名" htmlFor="me-name" required>
            <input
              id="me-name"
              value={displayName}
              maxLength={200}
              required
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </Field>
        </EditPanel>
      </Card>

      {/*
        Sign-in, between who you are and what you can reach - which is the order
        these are decided in. An operator can hand out a password when they
        create an account; this is where 「あとから変更できます」 becomes true.
      */}
      <PasswordCard hasPassword={session.user.hasPassword ?? false} />

      <Card
        title="アクセスできる範囲"
        caption="ここは自分では変更できません。変更が必要なときはクリニック管理者かJMCに依頼してください。"
      >
        {session.memberships.length === 0 ? (
          <Empty>
            {isPlatformAdmin
              ? 'JMC管理者はクリニックに所属しません。記事本文の閲覧にはサポート権限昇格が必要です。'
              : '所属がまだありません。クリニック管理者に追加を依頼してください。'}
          </Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">クリニック</th>
                <th scope="col">権限</th>
                <th scope="col">担当店舗</th>
              </>
            }
          >
            {session.memberships.map((m) => (
              <tr key={m.clinicId}>
                <td>{m.clinicName}</td>
                <td>
                  {m.clinicAdmin ? (
                    <Pill>{ROLE_LABEL.CLINIC_ADMIN}</Pill>
                  ) : m.stores.some((store) => store.admin) ? (
                    <Pill>{ROLE_LABEL.STORE_ADMIN}</Pill>
                  ) : (
                    <Pill tone="muted">{ROLE_LABEL.STORE_USER}</Pill>
                  )}
                </td>
                <td className="muted">
                  {m.stores.length === 0
                    ? '—'
                    : m.stores.map((store) => store.name).join('、')}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
